/**
 * Environment Storage
 *
 * @deprecated This module is deprecated. Use service-discovery module instead.
 *
 * Legacy storage functions for backward compatibility during migration.
 * New code should use the service-discovery module directly.
 */
import { getSelectedRegion, getCustomAppUrl, setSelectedRegion as setRegion, setCustomAppUrl as setCustomUrl, clearEnvironmentSelection, getRegionalEnvironment, REGIONAL_ENVIRONMENTS, } from './service-discovery';
// Legacy storage keys (kept for migration)
const LEGACY_ENVIRONMENT_STORAGE_KEY = 'airia-selected-environment';
const LEGACY_CUSTOM_URL_STORAGE_KEY = 'airia-custom-url';
/**
 * Map old environment names to new region codes
 */
const LEGACY_ENV_TO_REGION = {
    production: 'na',
    eu1: 'eu',
    australia: 'au',
    mena: 'mena',
    singapore: 'sg',
};
/**
 * @deprecated Use setSelectedRegion or setCustomAppUrl from service-discovery instead
 */
export function saveSelectedEnvironment(environment, customUrl) {
    console.warn('[DEPRECATED] saveSelectedEnvironment is deprecated. Use service-discovery module instead.');
    try {
        if (environment === 'custom' && customUrl) {
            setCustomUrl(customUrl);
        }
        else {
            // Map legacy environment names to region codes
            const region = LEGACY_ENV_TO_REGION[environment] || environment;
            setRegion(region);
        }
        // Also save to legacy keys for backward compatibility
        localStorage.setItem(LEGACY_ENVIRONMENT_STORAGE_KEY, environment);
        if (customUrl) {
            localStorage.setItem(LEGACY_CUSTOM_URL_STORAGE_KEY, customUrl);
        }
        else {
            localStorage.removeItem(LEGACY_CUSTOM_URL_STORAGE_KEY);
        }
    }
    catch (error) {
        console.error('Failed to save environment selection:', error);
    }
}
/**
 * @deprecated Use getSelectedRegion and getCustomAppUrl from service-discovery instead
 */
export function getSelectedEnvironment() {
    try {
        // First check new storage
        const region = getSelectedRegion();
        const customAppUrl = getCustomAppUrl();
        if (region) {
            // Map region code back to legacy environment name if needed
            const legacyEnv = Object.entries(LEGACY_ENV_TO_REGION).find(([, r]) => r === region)?.[0] || region;
            return { environment: legacyEnv, customUrl: customAppUrl };
        }
        // Fall back to legacy storage for migration
        const legacyEnvironment = localStorage.getItem(LEGACY_ENVIRONMENT_STORAGE_KEY);
        const legacyCustomUrl = localStorage.getItem(LEGACY_CUSTOM_URL_STORAGE_KEY);
        if (legacyEnvironment) {
            console.log('[Migration] Found legacy environment selection, migrating...');
            // Migrate to new storage
            if (legacyEnvironment === 'custom' && legacyCustomUrl) {
                setCustomUrl(legacyCustomUrl);
            }
            else {
                const newRegion = LEGACY_ENV_TO_REGION[legacyEnvironment] || legacyEnvironment;
                if (getRegionalEnvironment(newRegion)) {
                    setRegion(newRegion);
                }
            }
        }
        return { environment: legacyEnvironment, customUrl: legacyCustomUrl };
    }
    catch (error) {
        console.error('Failed to retrieve environment selection:', error);
        return { environment: null, customUrl: null };
    }
}
/**
 * @deprecated Use clearEnvironmentSelection from service-discovery instead
 */
export function clearSelectedEnvironment() {
    console.warn('[DEPRECATED] clearSelectedEnvironment is deprecated. Use clearEnvironmentSelection from service-discovery instead.');
    try {
        clearEnvironmentSelection();
        // Also clear legacy keys
        localStorage.removeItem(LEGACY_ENVIRONMENT_STORAGE_KEY);
        localStorage.removeItem(LEGACY_CUSTOM_URL_STORAGE_KEY);
    }
    catch (error) {
        console.error('Failed to clear environment selection:', error);
    }
}
/**
 * @deprecated Configuration is now handled by service discovery
 *
 * Generate configuration for a custom URL.
 * This function is kept for backward compatibility but should not be used in new code.
 */
export function generateCustomConfig(baseUrl) {
    console.warn('[DEPRECATED] generateCustomConfig is deprecated. Configuration is now handled by service discovery.');
    console.log('[generateCustomConfig] Input baseUrl:', baseUrl);
    const url = new URL(baseUrl);
    const domain = url.hostname;
    console.log('[generateCustomConfig] Parsed domain:', domain);
    // Domain to config file mapping for known domains (legacy support)
    const domainConfigMap = {
        localhost: 'local',
        'dev.office.airiadev.ai': 'dev',
        'dev.airiadev.ai': 'dev',
        'staging.office.airiadev.ai': 'staging',
        'staging.airiadev.ai': 'staging',
        'office.airia.ai': 'production',
        'eu1.office.airia.ai': 'eu1',
        'prodaus.office.airia.ai': 'australia',
        'mena.office.airia.ai': 'mena',
        'williams.office.airia.ai': 'williams',
        'williams.airia.ai': 'williams',
        'williams.identity.airia.ai': 'williams',
        'airia-office.lippomalls.com': 'lippo',
        'airia.lippomalls.com': 'lippo',
        'airia-identity.lippomalls.com': 'lippo',
        'demo.office.airia.ai': 'demo',
        'demo.airia.ai': 'demo',
        'demo.identity.airia.ai': 'demo',
        'internal.office.airia.ai': 'internal',
        'internal.airia.ai': 'internal',
        'internal.identity.airia.ai': 'internal',
        'btlaw.office.airia.ai': 'btlaw',
        'btlaw.airia.ai': 'btlaw',
        'btlaw.identity.airia.ai': 'btlaw',
    };
    // Check if this is a known domain
    if (domainConfigMap[domain]) {
        const configKey = domainConfigMap[domain];
        console.log('[generateCustomConfig] Matched known domain, using config:', configKey);
        const { getEnvironments } = require('./config/index');
        const environments = getEnvironments();
        return environments[configKey];
    }
    console.log('[generateCustomConfig] Unknown domain, generating dynamic config');
    // For truly custom domains, generate a dynamic config
    const baseOrigin = url.origin;
    const parts = domain.split('.');
    let tenantPrefix = '';
    if (parts.length >= 3 && parts[0] !== 'www') {
        tenantPrefix = parts[0];
    }
    const baseDomain = parts.slice(parts.length >= 3 ? -2 : 0).join('.');
    let apiDomain;
    let identityDomain;
    if (tenantPrefix && parts[1] === 'office') {
        apiDomain = `${tenantPrefix}.api.${baseDomain}`;
        identityDomain = `${tenantPrefix}.identity.${baseDomain}`;
    }
    else if (parts[0] === 'office') {
        apiDomain = `api.${baseDomain}`;
        identityDomain = `identity.${baseDomain}`;
    }
    else {
        apiDomain = `api.${baseDomain}`;
        identityDomain = `identity.${baseDomain}`;
    }
    const apiUrl = `https://${apiDomain}`;
    const authority = `https://${identityDomain}`;
    const dynamicConfig = {
        apiUrl,
        hubUrl: `https://${apiDomain}/browseragenthub`,
        authority,
        clientId: 'airia-office-add-in',
        popupPostLogoutRedirectUri: `${baseOrigin}/popup.html`,
        popupRedirectUri: `${baseOrigin}/popup.html`,
        redirectUri: baseOrigin,
    };
    console.log('[generateCustomConfig] Generated config:', dynamicConfig);
    return dynamicConfig;
}
/**
 * Get list of available regions (for UI components)
 */
export function getAvailableRegions() {
    return REGIONAL_ENVIRONMENTS.map((env) => ({
        value: env.region,
        label: env.name,
    }));
}
